<template>
  <component
    :is="route ? 'RouterLink' : 'button'"
    v-if="resource"
    v-bind="route ? { to: route } : { type: 'button' }"
    class="group flex w-full max-w-sm items-start gap-2 squircle-xl border border-gray-200 bg-white p-3 text-left shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
    @click="openModal"
  >
    <div
      class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300"
    >
      <FontAwesomeIcon :icon="['fal', resource.icon]" />
    </div>
    <div class="min-w-0 flex-1">
      <p
        class="truncate text-sm font-semibold text-gray-900 dark:text-gray-100"
      >
        {{ data.title }}
      </p>
      <p
        v-if="data.subtitle"
        class="truncate text-xs text-gray-500 dark:text-gray-400"
      >
        {{ data.subtitle }}
      </p>
      <dl v-if="fields.length">
        <div
          v-for="(field, index) in fields"
          :key="index"
          class="flex gap-1.5 text-xs"
        >
          <dt class="shrink-0 text-gray-400 dark:text-gray-500">
            {{ $t(field.label, field.label) }}
          </dt>
          <dd class="min-w-0 truncate text-gray-700 dark:text-gray-200">
            {{ field.value }}
          </dd>
        </div>
      </dl>
    </div>
    <FontAwesomeIcon
      :icon="['fal', 'chevron-right']"
      class="mt-1 shrink-0 text-gray-300 transition-transform group-hover:translate-x-0.5 dark:text-gray-600"
    />
  </component>
  <!-- Loaded on the first click only: the composer is heavy and most
       conversations never open it. Its Dialog renders over the chat. -->
  <component
    :is="mailComposer"
    v-if="mailComposer"
    ref="modalComposeMail"
    :title="data.title ?? ''"
    :model-notification-id="String(data.resource_id)"
    hide-button
  />
</template>

<script>
import { markRaw } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  WIDGET_RESOURCES,
  widgetModal,
  widgetRoute
} from './aiAssistantWidgets'

export default {
  name: 'AiAssistantWidgetCard',
  components: { FontAwesomeIcon },
  props: {
    message: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      mailComposer: null
    }
  },
  computed: {
    data() {
      return this.message.data ?? {}
    },
    resource() {
      return WIDGET_RESOURCES[this.data.resource_type]
    },
    route() {
      return this.resource
        ? widgetRoute(this.data.resource_type, this.data.resource_id)
        : null
    },
    modal() {
      return this.resource ? widgetModal(this.data.resource_type) : null
    },
    // A field without a value (a draft with no valid recipient) says nothing
    fields() {
      return (this.data.fields ?? []).filter((field) => field.value != null)
    }
  },
  methods: {
    async openModal() {
      if (this.modal !== 'mail_draft') {
        return
      }
      if (!this.mailComposer) {
        try {
          const { default: ModalComposeMail } =
            await import('@c/Mail/ModalComposeMail.vue')
          this.mailComposer = markRaw(ModalComposeMail)
        } catch {
          // Chunk unavailable (deployed meanwhile, network): say so rather
          // than leave a card that does nothing
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
          return
        }
        await this.$nextTick()
      }
      this.$refs.modalComposeMail.openModal()
    }
  }
}
</script>
